import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { RefreshCw, ExternalLink, ShieldAlert, CheckCircle2, AlertTriangle } from "lucide-react";

const VERDICT_META = {
  OK:              { label: "Live",        cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  PARTNER_ERROR:   { label: "Partner error", cls: "bg-red-100 text-red-800 border-red-200",           icon: ShieldAlert },
  HTTP_403:        { label: "Blocked (403)", cls: "bg-red-100 text-red-800 border-red-200",           icon: ShieldAlert },
  HTTP_429:        { label: "Rate limited",  cls: "bg-yellow-100 text-yellow-800 border-yellow-200",   icon: AlertTriangle },
  TINY_BODY:       { label: "Empty response",cls: "bg-amber-100 text-amber-800 border-amber-200",      icon: AlertTriangle },
  NETWORK_ERROR:   { label: "Unreachable",   cls: "bg-red-100 text-red-800 border-red-200",            icon: ShieldAlert },
  MISSING_URL:     { label: "No URL",        cls: "bg-neutral-100 text-neutral-800 border-neutral-200",icon: ShieldAlert },
};

function verdictMeta(v = "") {
  if (VERDICT_META[v]) return VERDICT_META[v];
  if (v.startsWith("HTTP_4") || v.startsWith("HTTP_5")) {
    return { label: v.replace("HTTP_", "HTTP "), cls: "bg-red-100 text-red-800 border-red-200", icon: ShieldAlert };
  }
  return { label: v, cls: "bg-neutral-100 text-neutral-800 border-neutral-200", icon: AlertTriangle };
}

export default function UrlHealthSection() {
  const [rows, setRows] = useState([]);
  const [checking, setChecking] = useState(false);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);
  const [filter, setFilter] = useState("all"); // all | ok | problems

  const load = async () => {
    try {
      const { data } = await api.get("/admin/url-health");
      setRows(data.results || []);
      const newest = (data.results || [])
        .map((r) => r._snapshot_at || r.checked_at)
        .filter(Boolean)
        .sort()
        .pop();
      setLastCheckedAt(newest || null);
    } catch (e) {
      toast.error("Failed to load URL health data");
    }
  };

  useEffect(() => { load(); }, []);

  const runCheck = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/admin/url-health/run");
      const sorted = [...(data.results || [])].sort((a, b) => {
        // problem rows first, then alphabetical
        const aOk = a.verdict === "OK", bOk = b.verdict === "OK";
        if (aOk !== bOk) return aOk ? 1 : -1;
        return (a.title || "").localeCompare(b.title || "");
      });
      setRows(sorted);
      setLastCheckedAt(data.checked_at);
      const oks = sorted.filter((r) => r.verdict === "OK").length;
      toast.success(`Checked ${data.checked} partners — ${oks} live, ${data.checked - oks} need attention`);
    } catch (e) {
      if (e?.response?.status === 409) toast.error("Another health check is already running");
      else toast.error("URL health check failed");
    } finally {
      setChecking(false);
    }
  };

  const filtered = rows.filter((r) => {
    if (filter === "ok") return r.verdict === "OK";
    if (filter === "problems") return r.verdict !== "OK";
    return true;
  });

  const summary = rows.reduce((acc, r) => { acc[r.verdict] = (acc[r.verdict] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-4" data-testid="url-health-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display text-2xl font-bold tracking-tight">Affiliate URL health</div>
          <div className="text-sm text-neutral-500 mt-1">
            Real HTTP check against each partner&apos;s tracker.
            {lastCheckedAt && (
              <span> Last run: {new Date(lastCheckedAt).toLocaleString()}</span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <div className="inline-flex rounded-lg border border-black/10 overflow-hidden text-xs">
            {["all", "ok", "problems"].map((f) => (
              <button
                key={f}
                type="button"
                data-testid={`url-health-filter-${f}`}
                onClick={() => setFilter(f)}
                className={`px-3 py-2 ${filter === f ? "bg-black text-white" : "bg-white text-neutral-600 hover:bg-neutral-50"}`}
              >
                {f === "all" ? `All (${rows.length})` : f === "ok" ? `Live (${summary.OK || 0})` : `Problems (${rows.length - (summary.OK || 0)})`}
              </button>
            ))}
          </div>
          <Button onClick={runCheck} disabled={checking} data-testid="url-health-recheck">
            <RefreshCw className={`w-4 h-4 mr-2 ${checking ? "animate-spin" : ""}`} />
            {checking ? "Checking…" : "Re-check all"}
          </Button>
        </div>
      </div>

      {rows.length === 0 && !checking && (
        <div className="border border-dashed border-black/10 rounded-xl p-10 text-center text-sm text-neutral-500">
          No health data yet. Click <b>Re-check all</b> to audit every active partner URL.
        </div>
      )}

      {filtered.length > 0 && (
        <div className="border border-black/10 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-black/10 text-neutral-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Brand</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Landing page title</th>
                <th className="text-left px-4 py-3">Final URL</th>
                <th className="text-right px-4 py-3">Master URL</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const m = verdictMeta(r.verdict);
                const Icon = m.icon;
                return (
                  <tr key={r.product_id} data-testid={`url-health-row-${r.product_id}`} className="border-b border-black/5 last:border-b-0 hover:bg-neutral-50/50">
                    <td className="px-4 py-3 align-top">
                      <div className="font-semibold text-neutral-900">{r.title}</div>
                      <div className="text-[11px] text-neutral-500">{r.provider || r.category}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <Badge variant="outline" className={`${m.cls} border inline-flex items-center gap-1`}>
                        <Icon className="w-3 h-3" /> {m.label}
                      </Badge>
                      {r.signals?.length > 0 && (
                        <div className="text-[11px] text-red-700 mt-1">signals: {r.signals.join(", ")}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top text-neutral-700">{r.landing_title || "—"}</td>
                    <td className="px-4 py-3 align-top max-w-[280px] truncate">
                      {r.final_url ? (
                        <a href={r.final_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                          {r.final_url.slice(0, 60)}<ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-neutral-400">{r.message || "—"}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top text-right max-w-[280px] truncate">
                      <a href={r.master_url} target="_blank" rel="noreferrer" className="text-neutral-500 hover:underline inline-flex items-center gap-1">
                        {(r.master_url || "").slice(0, 40)}<ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
